import type { useBylineConnectionsData } from './useBylineConnectionsData.ts';
import { useBylineConnectionsLoadTiming } from './useBylineConnectionsLoadTiming.ts';
import { useBylineConnectionsViewedEvent } from './useBylineConnectionsViewedEvent.ts';
import { useBylineTwgResultEvents } from './useBylineTwgResultEvents.ts';

type BylineConnectionsData = ReturnType<typeof useBylineConnectionsData>;

type BylineConnectionsAnalyticsOptions = {
	contentId: string;
	visibility: {
		hasConnection: boolean;
		hiddenBySsr: boolean;
		isResolving: boolean;
		shouldRenderByline: boolean;
	};
	data: {
		outgoing: Pick<BylineConnectionsData['outgoing'], 'links' | 'error'>;
		incoming: Pick<BylineConnectionsData['incoming'], 'twgLinks' | 'loading' | 'error'>;
		relatedContent: Pick<BylineConnectionsData['relatedContent'], 'links' | 'loading'>;
		devDocsConnection: BylineConnectionsData['devDocsConnection'];
	};
};

/**
 * Every analytics signal the byline itself owns: the load experience, the `byline viewed` event,
 * and the `twgResult` events. Returns the reporter to call when a click opens the menu.
 */
export const useBylineConnectionsAnalytics = ({
	contentId,
	visibility,
	data,
}: BylineConnectionsAnalyticsOptions): (() => void) => {
	const { outgoing, incoming, devDocsConnection } = data;
	// An incoming failure fails the load only when nothing outbound can show the byline instead.
	const hasOutboundConnection = outgoing.links.length > 0 || devDocsConnection !== null;
	useBylineConnectionsLoadTiming(
		contentId,
		visibility.isResolving,
		visibility.hasConnection,
		outgoing.error || (!hasOutboundConnection ? incoming.error : null),
	);
	useBylineConnectionsViewedEvent(visibility);
	return useBylineTwgResultEvents({ visibility, data });
};
